import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  XMarkIcon, 
  PlayIcon, 
  PauseIcon, 
  StopIcon, 
  ArrowPathIcon,
  XCircleIcon, 
  ExclamationTriangleIcon, 
  EyeIcon, 
  ChevronDownIcon, 
  ChevronRightIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { getExecutionStatusUrl, getExecutionCancelUrl } from '../../config/flowEngine';
import { useLanguage } from '../../contexts/LanguageContext';

interface ExecutionStep {
  nodeId: string;
  nodeType: string;
  nodeLabel: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  startTime?: Date;
  endTime?: Date;
  duration?: number;
  inputData?: any;
  outputData?: any;
  error?: string;
  progress?: number; // 0-1
}

interface ExecutionMonitorProps {
  executionId: string;
  flowNodes: Array<{
    id: string;
    type: string;
    data: {
      label: string;
    };
  }>;
  isVisible: boolean;
  onClose: () => void;
  onStop?: () => void;
  onPause?: () => void;
  onResume?: () => void;
}

const ExecutionMonitor: React.FC<ExecutionMonitorProps> = ({
  executionId,
  flowNodes,
  isVisible,
  onClose,
  onStop,
  onPause,
  onResume
}) => {
  const { t } = useLanguage();
  const [executionSteps, setExecutionSteps] = useState<ExecutionStep[]>([]);
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const [executionStatus, setExecutionStatus] = useState<'running' | 'completed' | 'failed' | 'paused'>('running');
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [totalDuration, setTotalDuration] = useState<number>(0);
  const [startTime, setStartTime] = useState<Date>(new Date());
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // Real execution data fetching (memoized)
  const fetchExecutionStatus = useCallback(async () => {
    if (!executionId || executionId.startsWith('demo-')) {
      setConnectionError('Invalid execution ID');
      setIsConnected(false);
      return;
    }
    
    try {
      setIsConnected(true);
      setConnectionError(null);
      
      // Get auth token
      const { auth } = await import('../../lib/firebase');
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(getExecutionStatusUrl(executionId), { headers });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Debug logging
      // console.log('Execution data received:', {
      //   execution_id: data.execution_id,
      //   status: data.status,
      //   current_node: data.current_node,
      //   completed_nodes: data.completed_nodes,
      //   failed_nodes: data.failed_nodes
      // });
      
      // Update execution status
      setExecutionStatus(data.status === 'completed' ? 'completed' : 
                        data.status === 'failed' ? 'failed' : 
                        data.status === 'running' ? 'running' : 'paused');
      
      setCurrentStep(data.current_node);
      
      // Update steps based on real data
      setExecutionSteps(prev => prev.map(step => {
        if (data.completed_nodes.includes(step.nodeId)) {
          return {
            ...step,
            status: 'completed',
            progress: 1,
            endTime: new Date(),
            duration: 2000, // Mock duration
            outputData: { result: 'Success', processed: true }
          };
        } else if (data.failed_nodes.includes(step.nodeId)) {
          return {
            ...step,
            status: 'failed',
            progress: 0,
            endTime: new Date(),
            duration: 1500,
            error: 'Node execution failed'
          };
        } else if (step.nodeId === data.current_node) {
          return {
            ...step,
            status: 'running',
            startTime: step.startTime || new Date(),
            progress: 0.5 // Mock progress for running node
          };
        }
        return step;
      }));
      
    } catch (error) {
      console.error('Failed to fetch execution status:', error);
      setConnectionError(error instanceof Error ? error.message : 'Connection failed');
      setIsConnected(false);
    }
  }, [executionId]);

  // Control commands
  const sendControlCommand = async (command: 'pause' | 'resume' | 'stop') => {
    if (!executionId) return;
    
    try {
      if (command === 'stop') {
        // Get auth token
        const { auth } = await import('../../lib/firebase');
        const user = auth.currentUser;
        const token = user ? await user.getIdToken() : null;
        
        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        await fetch(getExecutionCancelUrl(executionId), {
          method: 'POST',
          headers
        });
      }
      // TODO: Implement pause/resume endpoints in backend
      console.log(`Control command sent: ${command}`);
    } catch (error) {
      console.error('Failed to send control command:', error);
    }
  };

  // Real-time polling for execution status
  useEffect(() => {
    if (!isVisible || !executionId || executionId.startsWith('demo-')) return;

    // Initial fetch
    fetchExecutionStatus();

    // Stop polling if execution is completed or failed
    if (executionStatus === 'completed' || executionStatus === 'failed') {
      return;
    }

    // Poll every 2 seconds for updates
    const interval = setInterval(() => {
      fetchExecutionStatus();
    }, 2000);

    // Külön timer a duration frissítésére (smooth)
    const durationInterval = setInterval(() => {
      setTotalDuration(Date.now() - startTime.getTime());
    }, 100);

    return () => {
      clearInterval(interval);
      clearInterval(durationInterval);
    };
  }, [isVisible, executionId, startTime, executionStatus, fetchExecutionStatus]);

  // Initialize execution steps from flow nodes
  useEffect(() => {
    if (!isVisible || !executionId || !flowNodes.length) return;

    // Convert flow nodes to execution steps
    const executionSteps: ExecutionStep[] = flowNodes.map((node, index) => ({
      nodeId: node.id,
      nodeType: node.type,
      nodeLabel: node.data.label || `${node.type} (${node.id})`,
      status: index === 0 ? 'running' : 'pending', // First node starts running
      startTime: index === 0 ? new Date() : undefined,
      progress: index === 0 ? 0 : undefined,
      inputData: index === 0 ? { trigger_data: { executionId } } : undefined
    }));

    setExecutionSteps(executionSteps);
    setCurrentStep(flowNodes[0]?.id || null);
    setStartTime(new Date());
  }, [isVisible, executionId, flowNodes]);

  const getStatusIcon = (status: ExecutionStep['status']) => {
    switch (status) {
      case 'running':
        return <PlayIcon className="h-4 w-4 text-blue-500 animate-pulse" />;
      case 'completed':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="h-4 w-4 text-red-500" />;
      case 'skipped':
        return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />;
      default:
        return <ClockIcon className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: ExecutionStep['status']) => {
    switch (status) {
      case 'running':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      case 'skipped':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  const toggleStepExpansion = (nodeId: string) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Invalid Execution ID Warning */}
        {executionId.startsWith('demo-') && (
          <div className="bg-red-600 text-white px-6 py-3 text-center">
            <ExclamationTriangleIcon className="w-5 h-5 inline mr-2" />
            Érvénytelen execution ID. Csak valós flow végrehajtások monitorozhatók.
          </div>
        )}
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-500 to-purple-600">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <EyeIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">
                Flow Execution Monitor
              </h2>
              <div className="flex items-center space-x-4">
                <p className="text-white/80 text-sm">
                  Execution ID: {executionId}
                </p>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-red-400'}`}></div>
                  <span className="text-white/80 text-xs">
                    {isConnected ? 'Kapcsolódva' : 'Kapcsolat megszakadt'}
                  </span>
                  {connectionError && (
                    <span className="text-red-300 text-xs">({connectionError})</span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${executionStatus === 'running' ? 'bg-green-400' : executionStatus === 'completed' ? 'bg-blue-400' : 'bg-orange-400'}`}></div>
                  <span className="text-white/80 text-xs">
                    Status: {executionStatus === 'running' ? 'Futtatás' : executionStatus === 'completed' ? 'Befejezve' : executionStatus === 'paused' ? 'Szüneteltetve' : 'Hiba'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Control buttons */}
            {executionStatus === 'running' && (
              <>
                <button
                  onClick={() => {
                    sendControlCommand('pause');
                    onPause?.();
                  }}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-colors"
                  title="Pause Execution"
                >
                  <PauseIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    sendControlCommand('stop');
                    onStop?.();
                  }}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-colors"
                  title="Stop Execution"
                >
                  <StopIcon className="w-4 h-4" />
                </button>
              </>
            )}
            
            {executionStatus === 'paused' && (
              <button
                onClick={() => {
                  sendControlCommand('resume');
                  onResume?.();
                }}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-colors"
                title="Resume Execution"
              >
                <PlayIcon className="w-4 h-4" />
              </button>
            )}
            
            <button
              onClick={onClose}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-colors"
              title="Close Monitor"
            >
              ×
            </button>
          </div>
        </div>

        {/* Execution Summary */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div className="grid grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {executionSteps.filter(s => s.status === 'completed').length}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {executionSteps.filter(s => s.status === 'running').length}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Running</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-400">
                {executionSteps.filter(s => s.status === 'pending').length}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatDuration(totalDuration)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Duration</div>
            </div>
          </div>
        </div>

        {/* Execution Steps */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {executionSteps.map((step, index) => (
              <div
                key={step.nodeId}
                className={`border rounded-lg p-4 transition-all ${
                  step.nodeId === currentStep 
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 text-sm font-medium">
                      {index + 1}
                    </div>
                    {getStatusIcon(step.status)}
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {step.nodeLabel}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {step.nodeType}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(step.status)}`}>
                      {step.status}
                    </span>
                    
                    {step.duration && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDuration(step.duration)}
                      </span>
                    )}
                    
                    <button
                      onClick={() => toggleStepExpansion(step.nodeId)}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    >
                      {expandedSteps.has(step.nodeId) ? (
                        <ChevronDownIcon className="w-4 h-4" />
                      ) : (
                        <ChevronRightIcon className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Progress bar for running steps */}
                {step.status === 'running' && step.progress !== undefined && (
                  <div className="mt-3">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                      <span>Progress</span>
                      <span>{Math.round(step.progress * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${step.progress * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Expanded details */}
                {expandedSteps.has(step.nodeId) && (
                  <div className="mt-4 space-y-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                    {step.inputData && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Input Data
                        </h4>
                        <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-xs overflow-x-auto">
                          {JSON.stringify(step.inputData, null, 2)}
                        </pre>
                      </div>
                    )}
                    
                    {step.outputData && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Output Data
                        </h4>
                        <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-xs overflow-x-auto">
                          {JSON.stringify(step.outputData, null, 2)}
                        </pre>
                      </div>
                    )}
                    
                    {step.error && (
                      <div>
                        <h4 className="text-sm font-medium text-red-700 dark:text-red-300 mb-2">
                          Error
                        </h4>
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 rounded text-sm text-red-700 dark:text-red-300">
                          {step.error}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutionMonitor;
